import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings2, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import ThespianRoster from "@/components/thespian/ThespianRoster";
import { ApproveDialog, ReturnDialog } from "@/components/thespian/ThespianReviewDialogs";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import ThespianExportDialog from "@/components/thespian/ThespianExportDialog";
import ActivityLabel from "@/components/thespian/ActivityLabel";
import SubmissionReviewCard, { ReviewActions, EMPTY_TEXT, groupCounts, inGroup, useReveal, STICKY_CELL } from "@/components/review/SubmissionReviewCard";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly } from "@/lib/date";

const GROUPS = { pending: ["pending", "returned"], approved: ["approved"], declined: ["declined"] };
const COLUMNS = ["Member", "Date", "Activity", "Points", "Documentation", "Status", "Actions"];

function ClaimPoints({ c }) {
  if (c.status !== "approved") return <span className="font-bold tabular-nums text-amber-700">{fmtPts(c.points_claimed)}</span>;
  return <span className="font-bold tabular-nums text-emerald-700">{fmtPts(c.points_awarded)}{c.points_edited && <span className="ml-1 text-[11px] font-medium text-slate-400 line-through">{fmtPts(c.points_claimed)}</span>}</span>;
}

function ClaimRow({ c, viewOnly, onApprove, onDecline, onReturn }) {
  const open = GROUPS.pending.includes(c.status);
  return (
    <TableRow data-testid={`thespian-review-row-${c.id}`}>
      <TableCell className={`whitespace-nowrap text-sm font-medium text-slate-800 ${STICKY_CELL}`}>{c.member_name}</TableCell>
      <TableCell className="whitespace-nowrap text-sm tabular-nums">{formatDateOnly(c.activity_date)}</TableCell>
      <TableCell className="max-w-xs"><ActivityLabel c={c} /></TableCell>
      <TableCell><ClaimPoints c={c} /></TableCell>
      <TableCell className="max-w-xs text-sm text-slate-600">{c.notes}</TableCell>
      <TableCell className="max-w-[12rem]"><StatusBadge status={c.status} />{c.status === "returned" && <p className="mt-1 text-xs text-violet-700">{c.return_note}</p>}</TableCell>
      <TableCell>
        {open && !viewOnly ? (
          <ReviewActions onApprove={() => onApprove(c)} onDecline={() => onDecline(c)} onReturn={() => onReturn(c)}
            testIds={{ approve: `thespian-approve-${c.id}`, decline: `thespian-decline-${c.id}`, return: `thespian-return-${c.id}` }} />
        ) : (!open && c.reviewed_by_name ? <span className="text-xs text-slate-400">by {c.reviewed_by_name}</span> : null)}
      </TableCell>
    </TableRow>
  );
}

export default function ThespianReview() {
  const navigate = useNavigate();
  const { viewOnly } = useAuth();
  const [data, setData] = useState({ items: [], roster: [] });
  const [filter, setFilter] = useState("pending");
  const [approving, setApproving] = useState(null);
  const [returning, setReturning] = useState(null);

  const load = useCallback(() => { api.get("/thespian/review").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => groupCounts(GROUPS, data.items), [data.items]);
  const filtered = useMemo(() => data.items.filter((c) => inGroup(GROUPS, filter, c.status)), [data.items, filter]);
  const { visible, hasMore, loadMore } = useReveal(filtered);

  const decline = async (c) => {
    try {
      await api.post(`/thespian/claims/${c.id}/decline`);
      toast.success("Claim declined"); load(); window.dispatchEvent(new Event("rpg:badges-refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Thespian Points" subtitle="Review ITS point claims from your troupe"
        actions={<div className="flex flex-wrap gap-2">
          <ThespianExportDialog endpoint="/thespian/export" roster={data.roster} />
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/coach/thespian/activities")} data-testid="thespian-settings-btn"><Settings2 className="mr-2 h-4 w-4" /> Settings</Button>
        </div>} />

      <SubmissionReviewCard title="Point Claims" icon={ClipboardCheck} iconClass="bg-amber-100 text-amber-700 ring-1 ring-amber-200"
        filter={filter} onFilter={setFilter} counts={counts} columns={COLUMNS} rowCount={visible.length}
        emptyText={EMPTY_TEXT[filter]} hasMore={hasMore} onLoadMore={loadMore} testId="thespian-review">
        {visible.map((c) => <ClaimRow key={c.id} c={c} viewOnly={viewOnly} onApprove={setApproving} onDecline={decline} onReturn={setReturning} />)}
      </SubmissionReviewCard>

      <ThespianRoster roster={data.roster} />

      <ApproveDialog claim={approving} onClose={() => setApproving(null)} onDone={load} />
      <ReturnDialog claim={returning} onClose={() => setReturning(null)} onDone={load} />
    </div>
  );
}
